import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Tabs, Tab, Alert, Button, Spinner, Table, Badge } from 'react-bootstrap';
import { collection, query, where, getDocs, getFirestore, orderBy, limit } from 'firebase/firestore';
import firebaseApp from '../../firebase/credenciales';
import CurriculumExtractor from '../../components/agentes/CurriculumExtractor';
import { FaFileUpload, FaUserTie, FaHistory, FaSearch, FaCheckCircle, FaBriefcase } from 'react-icons/fa';

const CurriculumUploadScreen = ({ usuario }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [recentUploads, setRecentUploads] = useState([]);
  const [stats, setStats] = useState({
    totalCandidatos: 0,
    totalCurriculos: 0,
    totalConEmbeddings: 0
  });
  const [selectedVacante, setSelectedVacante] = useState(null);
  const [vacantes, setVacantes] = useState([]);
  
  const db = getFirestore(firebaseApp);
  
  // Obtener RUC del usuario autenticado
  const ruc = usuario?.ruc || "20538856674";

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruc]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Cargar vacantes activas
      await loadVacantes();
      
      // Cargar estadísticas generales
      await loadStats();
      
      // Cargar currículums recientes
      await loadRecentCurriculums();
    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadVacantes = async () => {
    try {
      const vacantesRef = collection(db, 'rs_vacantes');
      
      // Usar una consulta simplificada que no requiere índice compuesto
      const q = query(
        vacantesRef, 
        where('ruc', '==', ruc),
        where('estado', '==', 'activa')
      );
      
      const snapshot = await getDocs(q);
      
      // Ordenar los resultados en el cliente
      const vacantesList = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => {
          // Ordenar por fechaCreacion de más reciente a más antigua
          const dateA = a.fechaCreacion?.toDate?.() || new Date(a.fechaCreacion || 0);
          const dateB = b.fechaCreacion?.toDate?.() || new Date(b.fechaCreacion || 0);
          return dateB - dateA;
        });
      
      setVacantes(vacantesList);
      
      // Seleccionar la primera vacante por defecto si hay alguna
      if (vacantesList.length > 0 && !selectedVacante) {
        setSelectedVacante(vacantesList[0].id);
      }
    } catch (error) {
      console.error("Error cargando vacantes:", error);
    }
  };

  const loadStats = async () => {
    try {
      // Contar candidatos
      const candidatosRef = collection(db, 'candidatos');
      const candidatosQuery = query(candidatosRef, where('ruc', '==', ruc));
      const candidatosSnapshot = await getDocs(candidatosQuery);
      
      // Contar currículos procesados
      const procesadosRef = collection(db, 'procesamiento_curriculos');
      const procesadosQuery = query(procesadosRef, where('ruc', '==', ruc));
      const procesadosSnapshot = await getDocs(procesadosQuery);
      
      // Contar currículos con embeddings generados
      const embedsQuery = query(
        procesadosRef, 
        where('ruc', '==', ruc),
        where('status', '==', 'completed')
      );
      const embedsSnapshot = await getDocs(embedsQuery);
      
      setStats({
        totalCandidatos: candidatosSnapshot.size,
        totalCurriculos: procesadosSnapshot.size,
        totalConEmbeddings: embedsSnapshot.size
      });
    } catch (error) {
      console.error("Error cargando estadísticas:", error);
    }
  };

  const loadRecentCurriculums = async () => {
    try {
      const procesadosRef = collection(db, 'procesamiento_curriculos');
      // Simplificamos la consulta para evitar problemas de índice
      const q = query(
        procesadosRef, 
        where('ruc', '==', ruc)
      );
      
      const snapshot = await getDocs(q);
      
      // Para cada procesamiento, obtenemos info adicional del candidato
      const procesados = [];
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        
        // Obtener datos del candidato
        let candidatoData = null;
        if (data.candidatoId) {
          const candidatoSnapshot = await getDocs(
            query(collection(db, 'candidatos'), where('__name__', '==', data.candidatoId))
          );
          
          if (!candidatoSnapshot.empty) {
            candidatoData = candidatoSnapshot.docs[0].data();
          }
        }
        
        // Obtener datos de la vacante si existe
        let vacanteData = null;
        if (data.vacanteId) {
          const vacanteSnapshot = await getDocs(
            query(collection(db, 'rs_vacantes'), where('__name__', '==', data.vacanteId))
          );
          
          if (!vacanteSnapshot.empty) {
            vacanteData = vacanteSnapshot.docs[0].data();
          }
        }
        
        procesados.push({
          id: doc.id,
          ...data,
          candidato: candidatoData,
          vacante: vacanteData
        });
      }
      
      // Ordenamos en el cliente por fecha de procesamiento
      procesados.sort((a, b) => {
        const dateA = a.fechaProcesamiento?.toDate?.() || new Date(a.fechaProcesamiento || 0);
        const dateB = b.fechaProcesamiento?.toDate?.() || new Date(b.fechaProcesamiento || 0);
        return dateB - dateA;
      });
      
      // Limitamos a 10 resultados
      setRecentUploads(procesados.slice(0, 10));
    } catch (error) {
      console.error("Error cargando curriculums recientes:", error);
    }
  };

  const handleProcessingComplete = async () => {
    // Recargar datos después de un procesamiento exitoso
    await loadStats();
    await loadRecentCurriculums();
    
    // Cambiar a la pestaña de historial para mostrar el nuevo currículum procesado
    setActiveTab('history');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Fecha inválida";
    }
  };

  return (
    <Container fluid className="py-4">
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-primary text-white">
          <h4 className="mb-0">Procesamiento de Currículums</h4>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={8}>
              <p>
                Esta herramienta le permite procesar currículums en formato PDF para candidatos, 
                generando fragmentos y embeddings que permiten búsquedas semánticas inteligentes.
              </p>
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              {loading ? (
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Cargando...</span>
                </Spinner>
              ) : (
                <div className="text-end">
                  <h5>{stats.totalCandidatos} candidatos</h5>
                  <p className="text-muted mb-0">
                    {stats.totalCurriculos} currículums procesados<br/>
                    {stats.totalConEmbeddings} con embeddings
                  </p>
                </div>
              )}
            </Col>
          </Row>

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            <Tab 
              eventKey="upload" 
              title={
                <span>
                  <FaFileUpload className="me-2" />
                  Subir Currículum
                </span>
              }
            >
              <Card className="border-0">
                <Card.Body>
                  {vacantes.length > 0 ? (
                    <div className="mb-4">
                      <h5>Seleccione la vacante para este candidato (opcional)</h5>
                      <div className="d-flex flex-wrap gap-2 mb-3">
                        {vacantes.map(vacante => (
                          <Button
                            key={vacante.id}
                            variant={selectedVacante === vacante.id ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => setSelectedVacante(vacante.id)}
                            className="d-flex align-items-center"
                          >
                            <FaBriefcase className="me-2" />
                            <span>{vacante.titulo}</span>
                            {vacante.area && (
                              <Badge bg="info" className="ms-2" pill>
                                {vacante.area}
                              </Badge>
                            )}
                          </Button>
                        ))}
                        <Button
                          variant={selectedVacante === null ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setSelectedVacante(null)}
                        >
                          Sin vacante específica
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-4">
                      No hay vacantes activas. El currículum se procesará sin asociarlo a una vacante específica.
                    </Alert>
                  )}

                  {/* Integración del componente CurriculumExtractor */}
                  <CurriculumExtractor 
                    ruc={ruc}
                    vacanteId={selectedVacante}
                    onComplete={handleProcessingComplete}
                  />
                </Card.Body>
              </Card>
            </Tab>
            
            <Tab 
              eventKey="history" 
              title={
                <span>
                  <FaHistory className="me-2" />
                  Histórico de Procesamiento
                </span>
              }
            >
              <Card className="border-0">
                <Card.Body>
                  <h5 className="mb-4">Currículums procesados recientemente</h5>
                  
                  {recentUploads.length === 0 ? (
                    <Alert variant="info">
                      No hay currículums procesados recientemente.
                    </Alert>
                  ) : (
                    <Table responsive hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th>Candidato</th>
                          <th>Vacante</th>
                          <th>Fecha</th>
                          <th>Estado</th>
                          <th>Fragmentos</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentUploads.map(item => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="ms-2">
                                  <div className="fw-bold">{item.candidato?.nombre || 'Sin nombre'}</div>
                                  <div className="small text-muted">{item.candidato?.email || 'Sin email'}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {item.vacante ? (
                                <div>
                                  <Badge bg="info">
                                    <FaBriefcase className="me-1" />
                                    {item.vacante.titulo}
                                  </Badge>
                                  {item.vacante.area && (
                                    <div className="small text-muted mt-1">
                                      Área: {item.vacante.area}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted small">Sin vacante</span>
                              )}
                            </td>
                            <td>
                              {formatDate(item.fechaProcesamiento)}
                            </td>
                            <td>
                              {item.status === 'completed' ? (
                                <Badge bg="success">
                                  <FaCheckCircle className="me-1" />
                                  Completado
                                </Badge>
                              ) : (
                                <Badge bg="warning" text="dark">En proceso</Badge>
                              )}
                            </td>
                            <td>
                              {item.fragmentCount || 0}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline-primary"
                                  href={item.archivoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Ver PDF
                                </Button>
                                
                                <Button 
                                  size="sm" 
                                  variant="outline-info"
                                  onClick={() => {
                                    // Aquí podrías implementar la visualización de la información extraída
                                    alert("Funcionalidad para ver información extraída del currículum");
                                  }}
                                >
                                  <FaSearch className="me-1" />
                                  Explorar
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>
            
            <Tab 
              eventKey="candidates" 
              title={
                <span>
                  <FaUserTie className="me-2" />
                  Candidatos
                </span>
              }
            >
              <Card className="border-0">
                <Card.Body>
                  <Alert variant="info">
                    <h5>Próximamente</h5>
                    <p className="mb-0">
                      En esta sección podrá gestionar todos los candidatos y sus documentos asociados.
                    </p>
                  </Alert>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CurriculumUploadScreen;